import React, { useMemo, useState } from 'react';

import toast from 'react-hot-toast';

import { UseCarveFunctions } from 'features/deals/DealStages/EventActionCards/CarveDetailsCardBody/hooks';
import {
  CarveAction,
  CarveSpecification,
} from 'features/deals/DealStages/EventActionCards/CarveDetailsCardBody/types';
import { CarveSummary } from 'features/deals/DealStages/EventActionCards/CarveDiffTable/__generated__/fragments';
import { Button, CarveActions, CarveCardContainer, CarveName, Subheader, Spinner } from 'ui-kit';

import { CarveCard, CarveDetailsTable } from './CarveCard';
import { CreateCarveForm, Criteria } from './CreateCarveForm';
import CreateCarveQuestion from './CreateCarveQuestion';
import { criteriaToFilters } from './criteriaToFilterMapper';
import { useDealContext } from '../BiddingAndPricing/hooks/useDealContext';

import { GetFilterBounds_deal_performance_summary } from 'features/core/filter/FilterInputs';
interface TradeCarveManagerProps {
  dealId: string;
  carveSpecifications: CarveSpecification[];
  originalOffer: CarveSummary | null;
  filterBounds: GetFilterBounds_deal_performance_summary | undefined;
  onCarveAction: (action: CarveAction) => void;
  carveFunctions: UseCarveFunctions;
  onNoCarve: () => void;
}

const TradeCarveManager: React.FC<TradeCarveManagerProps> = ({
  dealId,
  carveSpecifications,
  originalOffer,
  filterBounds,
  onCarveAction,
  onNoCarve,
  carveFunctions,
}) => {
  const { offeringId, buyerCompanyId } = useDealContext(dealId);

  const [isInCreateCarvesMode, setIsInCreateCarvesMode] = useState<boolean>(
    carveSpecifications != null && carveSpecifications.length > 0,
  );

  const [selectedCarveId, setSelectedCarveId] = useState<string>();

  const selectedCarve = useMemo(
    () => carveSpecifications.find((carve) => carve.id === selectedCarveId),
    [carveSpecifications, selectedCarveId],
  );

  const { deleteDealCarve, deleteLoading, createDealCarve, createLoading } = carveFunctions;

  const handleCreateNewCarve = () => {
    const newId = '' + Date.now();
    onCarveAction({
      type: 'create-draft-carve',
      id: newId,
      name: '',
      criteria: [],
    });
    setSelectedCarveId(newId);
  };

  const handleSaveCarve = async (carveName: string, criteria: Criteria[]) => {
    const filters = criteriaToFilters(criteria);
    const draftId = selectedCarve?.id ?? '';
    try {
      const { data } = await createDealCarve({
        variables: {
          input: {
            deal_id: dealId,
            name: carveName,
            carve: filters,
          },
        },
      });
      const newId = data?.createDealCarve?.id;
      if (!newId) {
        throw new Error('CreateDealCarve returned no id');
      }
      onCarveAction({
        type: 'save-carve',
        id: draftId,
        name: carveName,
        newId,
      });
      setSelectedCarveId(newId);
    } catch (error) {
      console.error('[TradeCarveManager] createDealCarve failed', {
        dealId,
        carveName,
        error: error instanceof Error ? error.message : error,
      });
      toast.error('Could not save carve. Please try again.');
    }
  };

  const handleCriteriaChanged = (criteria: Criteria[]) => {
    if (selectedCarve === undefined) return;
    onCarveAction({
      type: 'update-carve',
      carveSpec: { ...selectedCarve, criteria },
    });
  };

  const handleRemoveCarve = async (carveId: string | undefined) => {
    if (carveId === undefined) return;

    const isDraftRemoval = carveSpecifications.some((c) => c.id === carveId && c.isDraft);
    if (isDraftRemoval) {
      setSelectedCarveId(undefined);
      onCarveAction({ type: 'delete-carve-complete', id: carveId });
      return;
    }

    if (!offeringId || !buyerCompanyId) {
      console.error('Missing offeringId or buyerCompanyId for carve deletion');
      toast.error('Could not remove carve: missing deal context. Please refresh and try again.');
      return;
    }

    setSelectedCarveId(undefined);

    onCarveAction({
      type: 'delete-carve',
      id: carveId,
    });

    try {
      await deleteDealCarve({
        variables: {
          id: carveId,
          offeringId: offeringId,
          buyerCompanyId: buyerCompanyId,
        },
      });
      onCarveAction({ type: 'delete-carve-complete', id: carveId });
    } catch (error) {
      console.error('[TradeCarveManager] deleteDealCarve failed', {
        dealId,
        carveId,
        offeringId,
        buyerCompanyId,
        error: error instanceof Error ? error.message : error,
      });
      toast.error('Could not remove carve. Please try again.');
      onCarveAction({ type: 'delete-carve-failed', id: carveId });
      setSelectedCarveId(carveId);
    }
  };

  if (originalOffer === null) {
    return null;
  }

  const onClickToNextCard = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onNoCarve();
  };

  const canClickNext =
    carveSpecifications != null &&
    carveSpecifications.length > 0 &&
    carveSpecifications.some((carve) => !carve.isDraft && !carve.isDeleted);

  return (
    <div className="text-slate-200">
      <Subheader>
        Create and save pool carves from the offering. Changes to selected carves auto-save. Select
        saved carves for pricing.
      </Subheader>
      {isInCreateCarvesMode ? (
        <>
          <div className="mt-4 flex flex-nowrap gap-x-3">
            <CreateCarveForm
              selectedCarve={selectedCarve}
              filterBounds={filterBounds}
              onSaveCarve={handleSaveCarve}
              disabled={!selectedCarve || createLoading}
              onCriteriaChanged={handleCriteriaChanged}
              isUpdating={selectedCarve?.isUpdating || false}
            />
            <div className="max-w-[calc(100%-400px)]">
              <CarveActions>
                <Button
                  data-testid="carve-create-button"
                  onClick={handleCreateNewCarve}
                  label={'create new carve'}
                >
                  Create New Carve
                </Button>
                <Button
                  data-testid="carve-remove-button"
                  type="secondary"
                  disabled={selectedCarveId === undefined || deleteLoading}
                  onClick={() => handleRemoveCarve(selectedCarveId)}
                  label={'Remove carve'}
                >
                  {deleteLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner loading={true} size={16} color="#f093ee" />
                      <span>Removing...</span>
                    </div>
                  ) : (
                    'Remove Carve'
                  )}
                </Button>
              </CarveActions>
              <div className="flex w-full flex-wrap items-start gap-2">
                <CarveCardContainer highlight={false}>
                  <CarveName>Original offer</CarveName>
                  <CarveDetailsTable carveSummary={originalOffer} />
                </CarveCardContainer>

                {carveSpecifications
                  ?.filter((carve) => !carve.isDeleted)
                  .map((carve) => {
                    const isSelected = carve.id === selectedCarveId;
                    return (
                      <CarveCardContainer
                        highlight={isSelected}
                        key={carve.id}
                        data-testid={`carve-card-${carve.id}`}
                      >
                        <CarveCard
                          carve={carve}
                          isSelected={isSelected}
                          onCarveSelected={() => setSelectedCarveId(carve.id)}
                        />
                      </CarveCardContainer>
                    );
                  })}
              </div>
            </div>
          </div>
          <Button
            data-testid="carve-next-button"
            label={'Next'}
            disabled={!canClickNext}
            onClick={onClickToNextCard}
            type="primary"
          >
            Next
          </Button>
        </>
      ) : (
        <CreateCarveQuestion
          setCreateCarve={setIsInCreateCarvesMode}
          onNoCarve={onClickToNextCard}
        />
      )}
    </div>
  );
};

export type { TradeCarveManagerProps };

export { TradeCarveManager };
